/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import type { CampaignData, CollectionKey } from '../../shared/types';

export interface HistoryItem {
  id: number;
  category: CollectionKey;
  name: string;
  timestamp: number;
}

interface NavigationContextType {
  navigateTo: (name: string) => void;
  addToHistory: (category: CollectionKey, id: number, name: string) => void;
  recentItems: HistoryItem[];
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
  campaignData: CampaignData;
  onNavigate: (category: CollectionKey, id: number) => void;
}

const HISTORY_KEY = 'campaign-navigation-history';

export function NavigationProvider({ children, campaignData, onNavigate }: NavigationProviderProps) {
  const [recentItems, setRecentItems] = useState<HistoryItem[]>(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const addToHistory = useCallback((category: CollectionKey, id: number, name: string) => {
    setRecentItems(prev => {
      // Remove duplicates of the same item
      const filtered = prev.filter(item => !(item.id === id && item.category === category));
      // Add new item to front, keep max 10
      const newHistory = [{
        id,
        category,
        name,
        timestamp: Date.now()
      }, ...filtered].slice(0, 10);
      
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  const navigateTo = useCallback((name: string) => {
    const term = name.toLowerCase().trim();
    if (!term) return;

    // Search order: NPCs, Locations, Organizations, PCs, Plots
    let foundId: number | undefined;
    let foundCategory: CollectionKey | undefined;
    let foundName: string | undefined;

    // NPCs
    const npc = campaignData.npcs.find(n => n.name.toLowerCase() === term);
    if (npc) {
      foundId = npc.id;
      foundCategory = 'npcs';
      foundName = npc.name;
    }

    // Locations
    if (!foundId) {
      const loc = campaignData.locations.find(l => l.name.toLowerCase() === term);
      if (loc) {
        foundId = loc.id;
        foundCategory = 'locations';
        foundName = loc.name;
      }
    }

    // Organizations
    if (!foundId) {
      const org = campaignData.organizations.find(o => o.name.toLowerCase() === term);
      if (org) {
        foundId = org.id;
        foundCategory = 'organizations';
        foundName = org.name;
      }
    }

    // PCs
    if (!foundId) {
      const pc = campaignData.playerCharacters.find(p => p.characterName.toLowerCase() === term);
      if (pc) {
        foundId = pc.id;
        foundCategory = 'playerCharacters';
        foundName = pc.characterName;
      }
    }
    
    // Plots
    if (!foundId) {
       const plot = campaignData.plotThreads.find(p => p.title.toLowerCase() === term);
       if (plot) {
         foundId = plot.id;
         foundCategory = 'plotThreads';
         foundName = plot.title;
       }
    }

    // Wiki Articles
    if (!foundId) {
      const article = campaignData.wikiArticles.find(a => a.title.toLowerCase() === term);
      if (article) {
        foundId = article.id;
        foundCategory = 'wikiArticles';
        foundName = article.title;
      }
    }

    if (foundId && foundCategory && foundName) {
      addToHistory(foundCategory, foundId, foundName);
      onNavigate(foundCategory, foundId);
    } else {
      console.warn(`Entity not found: ${name}`);
    }
  }, [campaignData, onNavigate, addToHistory]);

  return (
    <NavigationContext.Provider value={{ navigateTo, addToHistory, recentItems }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  return context; // Can be undefined
}
